<template>
  <v-card>
    <div class="event-log-details-view-io">
      <apio-button
        :button-size="closeButtonSize"
        button-type="selected"
        class="event-log-details-view-io__close-button"
        @click="$emit('closeIOView')"
        >Close
        <apio-icon
          class="ml-2"
          size="16"
          name="cross"
          lightModeColour="white"
          darkModeColour="white"
      /></apio-button>
      <div>
        <v-chip label x-small class="purple white--text tiny-extra-bold mr-2">{{
          step.ref
        }}</v-chip>
        <v-chip
          label
          x-small
          outlined
          class="purple purple--text tiny-extra-bold mr-2"
          >{{ step.label }}</v-chip
        >
      </div>
      <div
        class="event-log-details-view-io__header"
        :class="{ 'mb-2': step.data.type !== 'csv' }"
      >
          <img  :src="`/img/icons/${step.kind}_${step.type}.svg`" class="event-log-details-view-io__header--small-icon">
          <div class="event-log-details-view-io__header--text h1">
            {{ step.type }}
          </div>
          <v-spacer />
            <apio-icon
              size="16"
              class="event-log-details-view-io__payload--copy-icon ml-1"
              v-if="payload && step.data.type !== 'csv'"
              @click.native="copyToClipboard(payload)"
              lightModeColour="cyan"
              name="ui-copy"
            ></apio-icon>
        </div>
      </div>
      <div
        title="Copy to clipboard"
        style="
          max-width: 1440px;
          display: flex;
          justify-content: flex-end;
          margin-bottom: 16px;
        "
      >
        <v-spacer />
        <apio-button
          v-if="step.data.type === 'csv' && payload.headers && payload.items"
          button-size="small"
          @click.native="copyToClipboard(step.data.value)"
          >COPY TO CLIPBOARD
          <apio-icon
            size="24"
            class="ml-1"
            lightModeColour="white"
            darkModeColour="white"
            name="ui-copy"
          ></apio-icon>
        </apio-button>
      </div>
      <div
        class="small-extra-bold"
        v-if="step.type === 'http' && step.name.toLowerCase() === 'request'"
      >
        Request URL
      </div>

      <v-text-field
        class="my-4"
        :value="step.data.URL.value"
        dense
        outlined
        hide-details="auto"
        v-if="step.type === 'http' && step.name.toLowerCase() === 'request'"
        >Request
        <template slot="append">
          <apio-icon
            size="16"
            class="event-log-details-view-io__payload--copy-icon ml-1"
            @click.native="copyToClipboard(step.data.URL.value)"
            lightModeColour="cyan"
            name="ui-copy"
          ></apio-icon>
        </template>
      </v-text-field>
      <div
        class="small-extra-bold"
        v-if="step.type === 'http' && step.name.toLowerCase() === 'request'"
      >
        Request Body
      </div>

      <div
        id="editor"
        v-if="payload && step.data.type !== 'csv'"
        class="event-log-details-view-io__payload--json"
      />

      <data-table
        v-if="step.data.type === 'csv' && payload.headers"
        :headers="payload.headers"
        class="px-0"
        :items="payload.items"
        :hide-footer="true"
        :disable-pagination="true"
        item-key="ref"
        :loaded="true"
      />
    <apio-snackbar
      :text="snackbarText"
      :status="snackBarStatus"
      :snackbar="snackbar"
      @close="snackbar = false"
    />
  </v-card>
</template>

<script>
import * as clipboard from "clipboard-polyfill/text";
import loader from "@monaco-editor/loader";

function isObject(objValue) {
  return (
    objValue && typeof objValue === "object" && objValue.constructor === Object
  );
}

export default {
  name: "EventLogDetailViewIO",
  props: {
    lang: {
      type: String,
      default: "js",
    },
    step: {
      type: Object,
    },
  },
  components: {
    DataTable: () => import("@/components/DataTable/DataTable.vue"),
  },
  mounted() {
    this.wrapper = document.getElementById("editor");
    let monacoOptions = {
      theme: this.vsTheme,
      automaticLayout: true,
      autoIndent: true,
      contextmenu: false,
      formatOnType: true,
      readOnly: true,
      minimap: {
        enabled: false
      },
    };
    if (this.wrapper) {
      loader.init().then((monaco) => {
        if(this.isValidJSONString) {
          try {
            monacoOptions.value = JSON.stringify(JSON.parse(this.payload), null, "\t");
          } catch (e) {
            monacoOptions.value = this.payload;
          }
          monacoOptions.language = 'json';
          this.monacoEditor = monaco.editor.create(this.wrapper, monacoOptions);
        } else {
          monacoOptions.value = this.payload;
          monacoOptions.language = 'html'
          this.monacoEditor = monaco.editor.create(this.wrapper, monacoOptions);
        }
      });
    }
  },
  unmounted() {
    this.monacoEditor.dispose();
    this.wrapper = null;
  },
  data: () => ({
    snackbar: false,
    snackbarText: "",
    snackBarStatus: null,
    monacoEditor: null,
    wrapper: null,
  }),
  computed: {
    isValidJSONString() {
      try {
        JSON.stringify(this.payload);
      } catch (e) {
        return false;
      }
      return true;
    },
    payloadData() {
      return JSON.parse(this.payload);
    },
    isDarkMode() {
      return this.$store.state.mode.darkMode;
    },
    vsTheme() {
      return this.isDarkMode ? "vs-dark" : "vs";
    },
    isMobile() {
      return this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm;
    },
    headerIconSize() {
      return this.isMobile ? "32" : "48";
    },
    headerIconName() {
      const { type } = this.step;
      const resultType = this.step.data.type;
      switch (resultType || type) {
        default:
          return "nav-cog";
      }
    },
    closeButtonSize() {
      return this.isMobile ? "small" : "standard";
    },
    payload() {
      const { data, type } = this.step;
      const resultType = this.step.data.type;
      let name = this.step.name.toLowerCase();

      switch (resultType || type) {
        case "csv":
          if (data.value) {
            if (typeof data.value === "string") {
              data.value = JSON.parse(data.value);
            }
            let arraykeys = [];
            for (let key in data.value[0]) {
              arraykeys.push({ value: key, text: key });
            }
            console.log('arraykeys', arraykeys);
            return { headers: arraykeys, items: data.value };
          }
          break;
        case "http":
          if (name === "request") {
            // HTTP Request
            return `${data.Body.value || data.body.value}`;
          } else if (name === "result" || name === "response") {
            //HTTP Response
            console.log("http data", data);
            let value = [];
            if (Array.isArray(data)) {
              data.forEach((val) => {
                if (isObject(val)) {
                  value.push(JSON.stringify(val));
                }
              });
            }
            return value;
          } else {
            if (data.value == undefined) {
              if (typeof data === "string") {
                return data;
              }
              return JSON.stringify(data);
            }
            if (data.value !== undefined && isObject(data.value)) {
              return JSON.stringify(data.value);
            } else if (data.value !== undefined && typeof data.value == 'string') {
              return data.value;
            } else if (isObject(data)) {
              return JSON.stringify(data);
            } else {
              return data;
            }
          }
        case "object":
        case "senddata":
        default:
          console.log("def data", data);
          if (Array.isArray(data)) {
            // Array
            let value = [];
            if (data.length > 1) {
              // If more than 1 item
              data.forEach((val) => {
                if (isObject(val)) {
                  // If an object
                  value.push(JSON.stringify(val));
                } else {
                  value.push(val);
                }
              });
              return value;
            } else {
              if (data.value != undefined) {
                return JSON.stringify(data.value);
              }
              return data.toString();
            }
          } else {
            if (data.value == undefined) {
              if (typeof data === "string") {
                return data;
              }
              return JSON.stringify(data);
            }
            if (data.value !== undefined && isObject(data.value)) {
              return JSON.stringify(data.value);
            } else if (data.value !== undefined && typeof data.value == 'string') {
              return data.value;
            } else if (isObject(data)) {
              return JSON.stringify(data);
            } else {
              return data;
            }
          }
      }
      return data.value;
    },
  },
  methods: {
    prettySize(bytes, separator = "", postFix = "") {
      if (bytes) {
        const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
        const i = Math.min(
          parseInt(Math.floor(Math.log(bytes * 1024) / Math.log(1024)), 10),
          sizes.length - 1
        );
        return `${((bytes * 1024) / 1024 ** i).toFixed(i ? 1 : 0)}${separator}${
          sizes[i]
        }${postFix}`;
      }
      return "n/a";
    },
    async copyToClipboard(val) {
      const vm = this;
      await clipboard.writeText(val).then(
        function () {
          vm.snackbarText = "Copied to clipboard";
          vm.snackBarStatus = "success";
          vm.snackbar = true;
        },
        function () {
          vm.snackbarText = "Could not copy value to the clipboard";
          vm.snackBarStatus = "error";
          vm.snackbar = true;
        }
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.event-log-details-view-io {
  padding: 48px 20px 16px 48px;

  @media screen and (max-width: 768px) {
    padding: 40px 16px;
  }

  &__payload {
    margin-top: 16px;
    min-height: 537px;

    @media screen and (max-width: 768px) {
      padding: 8px;
      display: flex;
      height: calc(100vh - 166px);
    }

    &--copy-icon {
      border: 1px solid var(--apio-cyan);
      border-radius: 50%;
      cursor: pointer;
      width: 32px;
      height: 32px;
    }

    &--json {
      height: calc(100vh - 240px);
      margin: 0 auto;
      overflow-y: auto;
      width: calc(100vw - 40px);
    }
  }

  &__close-button {
    position: absolute;
    top: 16px;
    right: 16px;
    @media screen and (max-width: 768px) {
      top: 8px;
      right: 8px;
    }
  }

  &__header {
    display: flex;
    align-items: center;
    margin: 16px 0 0;

    &--small-icon {
      height: 56px;
      width: 56px;
    }

    &--icon {
      height: 64px;
      width: 64px;
      background-color: var(--apio-magenta);
      border-radius: 50%;

      @media screen and (max-width: 768px) {
        height: 40px;
        width: 40px;
      }
    }
    &--text {
      margin-left: 16px;
      color: var(--apio-shade-2);
      @media screen and (max-width: 768px) {
        font-size: var(--font-h2-size);
      }
    }
  }
}
</style>
